import { semanticColors, semanticGradients } from '@/theme/tokens';

/**
 * Shared styling for the answer-style surfaces, on the compact scale the
 * composer's Sources and Response options pop-ups use: 13/600 titles and
 * labels, 12px body, 11px hints, 12px radii.
 */

const FOCUS_RING = {
  '&:focus-visible': {
    outline: `2px solid ${semanticColors.brandPrimary}`,
    outlineOffset: '2px',
  },
} as const;

/** VoteMate's primary action — the header's Sign In / Sign Up pill. */
export const PRIMARY_PILL_SX = {
  // Shorthand padding: the global MuiButton override sets one that would win over px/py.
  padding: '0 16px',
  minHeight: 36,
  borderRadius: '999px',
  textTransform: 'none',
  fontSize: 13,
  fontWeight: 600,
  letterSpacing: '0.01em',
  whiteSpace: 'nowrap',
  backgroundColor: semanticColors.textPrimary,
  color: semanticColors.textOnPrimary,
  boxShadow: 'none',
  transition: 'background-color 0.2s ease',
  '&:hover': { backgroundColor: semanticColors.brandAccent, boxShadow: 'none' },
  '&.Mui-disabled, &[aria-disabled="true"]': {
    backgroundColor: semanticColors.textPrimary,
    color: semanticColors.textOnPrimary,
    opacity: 0.7,
    cursor: 'default',
  },
  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  ...FOCUS_RING,
} as const;

/** The quiet companion action ("Not now", "Cancel"). */
export const QUIET_BUTTON_SX = {
  padding: '0 12px',
  minHeight: 36,
  borderRadius: '999px',
  textTransform: 'none',
  fontSize: 13,
  fontWeight: 600,
  color: semanticColors.textSecondary,
  '&:hover': { backgroundColor: semanticColors.surfaceSubtle },
  '&[aria-disabled="true"]': { opacity: 0.6, cursor: 'default' },
  ...FOCUS_RING,
} as const;

/** Small uppercase section label, as the ballot's section headers read. */
export const SECTION_LABEL_SX = {
  fontSize: 11,
  fontWeight: 600,
  lineHeight: 1.4,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: semanticColors.textTertiary,
} as const;

/** A selectable option card. The border thickens by inset shadow, so nothing shifts. */
export function optionCardSx(selected: boolean) {
  return {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: 0,
    overflowWrap: 'anywhere',
    padding: '10px 12px',
    paddingInlineEnd: '32px',
    borderRadius: '12px',
    cursor: 'pointer',
    border: `1px solid ${selected ? semanticColors.textBrand : semanticColors.borderLight}`,
    boxShadow: selected ? `inset 0 0 0 1px ${semanticColors.textBrand}` : 'none',
    backgroundColor: selected ? semanticColors.backgroundSecondary : semanticColors.surface,
    transition: 'border-color 0.15s ease, background-color 0.15s ease',
    '&:hover': { borderColor: selected ? semanticColors.textBrand : semanticColors.borderMedium },
    '&:has(input:focus-visible)': {
      outline: `2px solid ${semanticColors.brandPrimary}`,
      outlineOffset: '2px',
    },
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  } as const;
}

/** The brand-gradient disc behind the sparkles mark. */
export const GRADIENT_MARK_SX = {
  width: 28,
  height: 28,
  flexShrink: 0,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: semanticGradients.primary,
  color: semanticColors.textOnPrimary,
} as const;
